import { Controller } from "stimulus";
import { track } from '../analytics';

export default class extends Controller {
  static targets = [
    "add",
    "remove",
    "verify",
    "accountList",
    "error",
  ];

  static DEFAULT_ERROR =
    "We could not finish linking that bank account. Please try again, or contact support.";

  // textContent, never innerHTML: this string can carry a server message and must
  // not be able to inject markup into the page.
  showError(message) {
    const target = this.errorElement();
    target.textContent = message || this.constructor.DEFAULT_ERROR;
    target.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  // The error target is optional so this controller keeps working on views that
  // have not declared one yet. Create a slot on demand when it is missing.
  errorElement() {
    if (this.hasErrorTarget) return this.errorTarget;

    if (!this._fallbackError) {
      this._fallbackError = document.createElement("p");
      this._fallbackError.className = "text-sm text-red-600 my-2";
      this._fallbackError.setAttribute("role", "alert");
      this.accountListTarget.parentNode.insertBefore(
        this._fallbackError,
        this.accountListTarget
      );
    }

    return this._fallbackError;
  }

  remove(event) {
    event.preventDefault();
    this.removeTarget.disabled = true;
    this.removeTarget.textContent = "Removing...";

    fetch("/profile/remove_bank_account", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        payment_method_id: event.target.dataset.paymentMethodId,
      }),
    })
      .then((response) => response.json())
      .then(({ ach_methods }) => {
        this.accountListTarget.innerHTML = ach_methods;
      });
  }

  verify(event) {
    event && event.preventDefault();
    track("VerifyPaymentInfo");
    const url = `/contributions/fetch_link_token?access_token=${event.target.dataset.accessToken}`;
    this.fetchTokenAndAddAccount(url, event);
  }

  addNew(event) {
    event && event.preventDefault();
    track("AddPaymentInfo");
    this.fetchTokenAndAddAccount('/contributions/fetch_link_token', event);
  }

  fetchTokenAndAddAccount (url, event) {
    let linkToken;

    fetch(url)
      .then((response) => response.json())
      .then(({ token }) => {
        linkToken = token;
        const handler = Plaid.create({
          token: token,
          onSuccess: (public_token, metadata) => {
            // Pass public_token to backend
            fetch("/profile/add_bank_account", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document
                  .querySelector('meta[name="csrf-token"]')
                  .getAttribute("content"),
              },
              body: JSON.stringify({
                public_token,
                metadata,
                old_access_token: event.target.dataset.accessToken
              })
            })
              .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
              .then(({ ok, body }) => {
                // A bank account that failed to record its ACH mandate cannot be
                // debited, so the server refuses it rather than attaching an
                // account that would fail on every contribution. Say so, instead
                // of leaving the list silently unchanged.
                if (!ok || body.error) {
                  this.showError(body.error);
                  return;
                }

                this.clearError();
                this.accountListTarget.innerHTML = body.ach_methods;
              })
              .catch((error) => {
                console.error('error', error)
                this.showError();
              })
          },
          onLoad: () => {
            handler.open()
          },
          onExit: (err, metadata) => {
            console.error('err', err)
            console.error('metadata', metadata)
          },
          onEvent: (eventName, metadata) => { },
          receivedRedirectUri: null
        })
      })
  }
}
